import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  toCanonicalMeters, fromCanonicalMeters,
  toCanonicalInches, fromCanonicalInches,
} from "@/lib/units";

/**
 * Generic pill-toggle unit input. Used for distance (m / ft / in) and
 * height (in / ft). The value passed in is in the *current display unit*;
 * the parent decides when to convert to canonical via lib/units helpers.
 *
 * Props:
 *   kind     "distance" | "height"
 *   value    number in the current display unit (or null/"")
 *   unit     current unit ("m"|"ft"|"in" for distance, "in"|"ft" for height)
 *   onChange (newValue, newUnit) => void
 *   label    optional label text (defaults to "Distance" / "Height")
 *   testId   optional, applied to the <input>
 */
export default function UnitInput({
  kind = "distance",
  value, unit, onChange,
  label,
  testId,
  step = "0.1",
  className = "",
  disabled = false,
}) {
  const opts = kind === "height" ? ["in", "ft"] : ["m", "ft", "in"];
  const fallback = kind === "height" ? "in" : "m";
  const u = opts.includes(unit) ? unit : fallback;
  const heading = label || (kind === "height" ? "Height" : "Distance");

  const handleValue = (e) => {
    const v = e.target.value;
    onChange(v === "" ? null : Number(v), u);
  };

  const handleToggle = (newUnit) => {
    if (newUnit === u) return;
    let converted = value;
    if (value !== null && value !== undefined && value !== "") {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        if (kind === "height") {
          const inches = toCanonicalInches(n, u);
          converted = fromCanonicalInches(inches, newUnit);
        } else {
          const meters = toCanonicalMeters(n, u);
          converted = fromCanonicalMeters(meters, newUnit);
        }
      }
    }
    onChange(converted, newUnit);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2">
        <Label className="hs-label">{heading}</Label>
        <div className="inline-flex bg-muted2 rounded-lg p-0.5" role="radiogroup" aria-label={`${heading} unit`}>
          {opts.map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={u === opt}
              onClick={() => handleToggle(opt)}
              disabled={disabled}
              className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                u === opt ? "bg-surface text-ink shadow-sm" : "text-ink-3 hover:text-ink"
              }`}
              data-testid={testId ? `${testId}-unit-${opt}` : `${kind}-unit-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        onChange={handleValue}
        disabled={disabled}
        className="mt-2 h-11 rounded-xl font-data"
        data-testid={testId}
      />
    </div>
  );
}
